import React, { FunctionComponent, useEffect } from 'react';

import { Divider } from '@yandex-360/orb-components/dist/components/Divider';
import cn from 'classnames';
import { useSelector } from 'react-redux';

import {
    selectIsTeleMessengerConferencesHistory,
    selectIsTelemostDesktopIntegration,
} from '../../shared/features/config/selectors';
import { clearNotifications } from '../../shared/features/notifications/ps-notifications';
import { parentFrameCommunicator } from '../../shared/features/parent-frame-communicator';
import { countMetrika } from '../../shared/helpers/metrika';
import { replace, SCREENS } from '../../shared/helpers/navigation';
import { useActions } from '../../shared/hooks';
import { OutgoingEvents } from '../call/iframe-sagas/types';
import { CONFERENCE_HISTORY_PAGE_ID } from './constants';
import { ConferenceHistoryList } from './features/history-items/components/list';
import { OneColHeader } from './features/history-items/components/one-col-header';
import { TwoColHeader } from './features/history-items/components/two-col-header';
import { ArtifactsSidebar } from './features/sidebar';
import { setSelectedSessionId } from './features/sidebar/slice';
import { useIsDesktopOneColLayout, useIsMobileOneColLayout } from './hooks';
import {
    selectIsEmptyResult,
    selectIsFullFetchingFailed,
    selectSelectedSession,
    selectSelectedSessionId,
} from './selectors';
import styles from './styles.css';
import { useSelectedSessionHistory } from './use-selected-session-history';

export const ConferencesHistoryPage: FunctionComponent = () => {
    const selectedSession = useSelector(selectSelectedSession);
    const selectedSessionId = useSelector(selectSelectedSessionId);
    const isResultEmpty = useSelector(selectIsEmptyResult);
    const isFullFetchingFailed = useSelector(selectIsFullFetchingFailed);
    const isTeleMessengerConferencesHistory = useSelector(selectIsTeleMessengerConferencesHistory);

    const actions = useActions({ clearNotifications, setSelectedSessionId });

    useSelectedSessionHistory({
        enabled: isTeleMessengerConferencesHistory,
        selectedSessionId,
        onSelectedSessionIdChange: actions.setSelectedSessionId,
    });

    const isDesktopOneColLayout = useIsDesktopOneColLayout();

    const isMobileOneColLayout = useIsMobileOneColLayout();

    useEffect(() => {
        countMetrika('meeting_history', 'show');
    }, []);
    const isTelemostDesktopIntegration = useSelector(selectIsTelemostDesktopIntegration);

    const handleBackButtonClick = () => {
        if (isTeleMessengerConferencesHistory) {
            parentFrameCommunicator.sendMessage('messenger', {
                event: OutgoingEvents.BACK_BUTTON_CLICK,
            });

            return;
        }

        actions.clearNotifications();
        replace(SCREENS.CREATE_CALL);
    };

    return (
        <div
            id={CONFERENCE_HISTORY_PAGE_ID}
            className={cn(styles.ConferenceHistoryPage, {
                [styles.desktopOneColLayout]: isDesktopOneColLayout,
                [styles.mobileOneColLayout]: isMobileOneColLayout,
                [styles.stub]: isResultEmpty || isFullFetchingFailed,
                [styles.telemostDesktopIntegration]: isTelemostDesktopIntegration,
                [styles.privateConferenceHistoryPage]: isTeleMessengerConferencesHistory,
            })}
        >
            <div className={styles.wrapper}>
                <div className={styles.left}>
                    {isMobileOneColLayout && <OneColHeader onBackButtonClick={handleBackButtonClick} />}
                    {!isMobileOneColLayout && (
                        <>
                            <TwoColHeader onBackButtonClick={handleBackButtonClick} />
                            <Divider size="sm" />
                        </>
                    )}
                    <ConferenceHistoryList />
                </div>
                {selectedSession && (
                    <div className={styles.right}>
                        <ArtifactsSidebar />
                    </div>
                )}
            </div>
        </div>
    );
};
